import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {API_URL} from '../../../settings/local_settings';
import {APIClient} from '../../../utils/APIClient';
import {refetchPortalSubscriptionGating} from '../../../utils/usePortalSubscriptionGating';
import {FaIcon} from '../../FaIcon';
import {Spinner} from '../../Spinner';
import {InviteUserForm} from '../../invitations/InviteUserModal';
import {portalInviteFeatures} from '../portal_modules/portalInviteFeatures';

/**
 * Botón (+ modal) para invitar al cliente a gestionar sus propias conexiones desde la ficha del
 * cliente. Reusa InviteUserForm con el cliente fijo.
 * El link de invitación es el vector de onboarding logueado del cliente.
 *
 * Los permisos salen del PORTAL de ese cliente, igual que la invitación de la pestaña Portal, más
 * «Gestionar sus conexiones», que es para lo que existe este botón. Hasta el 02/10 iban fijos en el
 * código (todo apagado menos Conexiones), y al aceptar el backend los guardaba como excepciones por
 * usuario: la persona quedaba sin Informes ni MCP aunque el portal se los diera (ver
 * `portalInviteFeatures`). Por eso el formulario no se monta hasta leer el portal: `InviteUserForm`
 * toma los permisos en su constructor, y sembrar con un default para corregirlo después dejaría el
 * default.
 */
export const InviteClientToManageConnectionsButton = ({clientId, onInvited}) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    // `null` = leyendo el portal; un objeto = los permisos con los que se siembra el formulario.
    const [features, setFeatures] = useState(null);
    // El idioma del portal del cliente, que el formulario trae elegido (sale del mismo pedido).
    const [portalLanguage, setPortalLanguage] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    // «Reintentar» desaparece con el aviso: el foco que tenía se perdería en el `<body>`. Si se
    // llegó al formulario reintentando, el foco va al correo.
    const [retried, setRetried] = useState(false);
    // Sólo cuenta la respuesta del último pedido: cerrar y reabrir rápido no puede sembrar el
    // formulario nuevo con la respuesta del anterior.
    const requestRef = useRef(0);

    useEffect(() => () => { requestRef.current += 1; }, []);

    const loadPortalModules = useCallback(() => {
        requestRef.current += 1;
        const request = requestRef.current;
        setFeatures(null);
        setLoadFailed(false);
        const settle = (config) => {
            if (request !== requestRef.current) return;
            // Sin `modules` no se siembra con un default: sería volver a mandar permisos que el
            // portal no da, que es el bug que este componente vino a cerrar.
            if (config && config.modules) {
                setPortalLanguage(config.language || null);
                setFeatures(portalInviteFeatures(config.modules, {connections: true}));
            } else {
                setLoadFailed(true);
            }
        };
        new APIClient(API_URL + `client/${clientId}/portal/config/`).call('GET')
            .then((response) => settle(response && response.status === 'OK' ? response.data : null))
            .catch(() => settle(null));
    }, [clientId]);

    const openModal = (e) => {
        e.stopPropagation();
        setOpen(true);
        setRetried(false);
        loadPortalModules();
    };

    const retry = () => {
        setRetried(true);
        loadPortalModules();
    };

    const close = () => {
        requestRef.current += 1;
        setOpen(false);
    };

    const renderBody = () => {
        // Misma armazón que el formulario: el aviso dentro de `modal-section` (el `.modal-body`
        // va sin padding) y la acción en el pie, donde el formulario pone «Enviar». Un `.btn`
        // suelto en el cuerpo es `display:flex` y se estira a todo el ancho.
        if (loadFailed) {
            return (
                <>
                    <ModalBody>
                        <div className="modal-section">
                            <div className="invite-info-callout -warning" role="alert">
                                <FaIcon iconName="exclamation-triangle" className="invite-info-callout-icon"/>
                                <span>{t('invite_user.seed_load_error')}</span>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn -alt" onClick={retry}>
                            {t('invite_user.seed_load_retry')}
                        </button>
                    </ModalFooter>
                </>
            );
        }
        if (!features) {
            return (
                <ModalBody>
                    <div className="box-loader"><Spinner color="positive"/></div>
                </ModalBody>
            );
        }
        return (
            <InviteUserForm
                clientOnly
                fixedClientId={clientId}
                defaultClientFeatures={features}
                defaultPortalLanguage={portalLanguage}
                featuresNote={t('invite_user.portal_seed_note')}
                autoFocusEmail={retried}
                afterSubmit={() => {
                    setOpen(false);
                    // La invitación ocupa un lugar del cupo de usuarios del portal: el próximo
                    // modal (éste o el de la pestaña Portal) tiene que leerlo actualizado.
                    refetchPortalSubscriptionGating();
                    if (onInvited) onInvited();
                }}
            />
        );
    };

    return (
        <>
            <button className="btn -alt -negative" onClick={openModal}>
                <FaIcon iconName="plug"/> {t('client_ad_account_stores_list.invite_to_manage_connections')}
            </button>
            {/* `scrollable` + `invite-portal-modal`, como el modal de la pestaña Portal: con el aviso
                de cupo lleno encima, el formulario no entra en la pantalla y «Enviar» quedaba
                fuera de la vista. */}
            <Modal isOpen={open} toggle={close} scrollable className="invite-portal-modal">
                <ModalHeader toggle={close}>
                    <FaIcon iconName="user-plus"/>
                    {t('client_ad_account_stores_list.invite_to_manage_connections_title')}
                </ModalHeader>
                {/* Sin `open &&`: reactstrap sigue dibujando el contenido durante el fundido de
                    cierre, y cortarlo ahí encogía el modal al encabezado mientras se iba. Al
                    reabrir, `openModal` vuelve al spinner en el mismo render. */}
                {renderBody()}
            </Modal>
        </>
    );
};
